import { Application, Container, Graphics, Text } from "pixi.js";
import type { TaskDefinition } from "@talent/protocol";
import { DEMO_FORM, EMPTY, type SudokuPuzzle } from "@talent/tasks";
import {
  SessionRecorder,
  TouchTracker,
  detectMultiTouch,
} from "@talent/client-telemetry";
import {
  ABANDONED,
  TIMED_OUT,
  untilAbandoned,
  animate,
  burst,
  easeOutBack,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilSettled,
  wait,
} from "./game-feel.js";
import { ensureGlyphFont, numberGlyph } from "./glyph.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { COLOR, familyColor, lighten, mix, touchMin } from "./paint.js";
import { TOKEN_COLORS } from "./token.js";

/**
 * Sudoku — every row, every column, every box, one of each.
 *
 * The only game here where the child rules things OUT, and the only one where **being wrong is
 * information rather than a mark against you**: an illegal symbol is refused at the moment it is
 * tried, with the squares that already hold it lit up. That is the lesson of the game, and it is
 * why the round cannot end with a wrong grid on the board (R-5).
 *
 * Which means completion alone says nothing — every finished grid is the solution. What separates
 * one round from another is how many times the child tried something impossible, so that is what is
 * counted and scored against `refusalBudget`.
 *
 * **Two taps, always in the same order:** pick a square, then pick a symbol. A drag would be lighter
 * for an adult and is the wrong choice for a five-year-old on a phone — a drag that starts on a
 * scrollable page fights the page, and a dropped drag has no obvious undo. Tapping a filled square
 * clears it, which is the escape a child needs when they have painted themselves into a corner.
 */

export interface SudokuTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generatePuzzle: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => SudokuPuzzle;
  /** Whether a symbol may go in a square, given what is on the board. */
  placementAllowed: (
    puzzle: SudokuPuzzle,
    grid: readonly number[],
    cell: number,
    value: number,
  ) => boolean;
  scoreTrial: (
    puzzle: SudokuPuzzle,
    filled: readonly number[],
    refusals: number,
  ) => boolean;
  signal?: AbortSignal;
  showTutorial?: boolean;
  onTutorialComplete?: () => Promise<"replay" | "start">;
  /**
   * The level to open at, from the server's suggestion for this child.
   *
   * Absent for free play, which opens at the game's own floor. Clamped into range rather than
   * trusted: it is derived from an objective's level window, which lives in a different file from
   * this task's ceiling.
   */
  startAt?: number;
}

export async function runSudokuTask(opts: RunOptions): Promise<SudokuTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  const idle = (ms: number) => wait(ms, opts.signal);

  // Pixi rasterises text on creation and caches it, so a numeral created before Vazirmatn has
  // loaded is drawn in the fallback face for the rest of the sitting.
  await ensureGlyphFont();

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    const bands = 24;
    for (let i = 0; i < bands; i++) {
      backdrop
        .rect(0, (h / bands) * i, w, h / bands + 1)
        .fill(mix(COLOR.stage, COLOR.stageDeep, i / bands));
    }
    backdrop.circle(w / 2, h * 0.34, Math.min(w, h) * 0.55).fill({ color: accent, alpha: 0.07 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  /**
   * A symbol, drawn as one of six shapes or as a numeral.
   *
   * Shapes reuse the token palette so a symbol looks the same here as in every other game that draws
   * one. Six shapes covers a 6×6, which is the largest grid this game goes to.
   */
  function drawSymbol(
    parent: Container,
    value: number,
    size: number,
    digits: boolean,
    tint: number,
  ): Graphics | Text {
    if (digits) {
      // One-based on screen: a child reading numerals expects a sudoku to start at one, and the
      // zero-based index is an implementation detail of the generator.
      const glyph = numberGlyph(value + 1, { size: size * 0.62, color: tint });
      parent.addChild(glyph);
      return glyph;
    }

    const g = new Graphics();
    const color = TOKEN_COLORS[value % TOKEN_COLORS.length] ?? accent;
    const paint = tint === COLOR.muted ? mix(color, COLOR.stage, 0.45) : color;
    const r = size * 0.28;
    switch (value % 6) {
      case 0:
        g.circle(0, 0, r).fill(paint);
        break;
      case 1:
        g.roundRect(-r, -r, r * 2, r * 2, r * 0.35).fill(paint);
        break;
      case 2:
        g.poly([0, -r, r, r * 0.8, -r, r * 0.8]).fill(paint);
        break;
      case 3:
        g.poly([0, -r, r, 0, 0, r, -r, 0]).fill(paint);
        break;
      case 4: {
        const pts: number[] = [];
        for (let i = 0; i < 10; i++) {
          const a = (Math.PI / 5) * i - Math.PI / 2;
          const rad = i % 2 === 0 ? r : r * 0.45;
          pts.push(Math.cos(a) * rad, Math.sin(a) * rad);
        }
        g.poly(pts).fill(paint);
        break;
      }
      default:
        // A plus, which is distinct in silhouette from all five above.
        g.rect(-r * 0.32, -r, r * 0.64, r * 2).fill(paint);
        g.rect(-r, -r * 0.32, r * 2, r * 0.64).fill(paint);
        break;
    }
    parent.addChild(g);
    return g;
  }

  interface Trial {
    solved: Promise<{ grid: number[]; refusals: number }>;
    /**
     * Solves the puzzle as a finger would: pick the missing shape and watch it sit down.
     *
     * Used only by the demonstration, and it shows the interaction the game now HAS. It used to select
     * a square and then a shape, which taught an order the game no longer requires — a demonstration
     * of a removed step is worse than none, because a child would copy it.
     */
    demonstrate: () => Promise<void>;
    cleanup: () => void;
  }

  function present(puzzle: SudokuPuzzle): Trial {
    const layer = new Container();
    stage.addChild(layer);

    const grid = [...puzzle.given];
    const blanks = new Set(puzzle.blanks);
    let selected: number | null = null;
    let refusals = 0;
    /** Squares lit red because they already hold the symbol just refused. */
    let conflicts: number[] = [];
    /**
     * A shape the child picked that fits more than one square, and the squares it fits.
     *
     * The order of the two taps is no longer forced: picking a shape first is the natural thing to do
     * and now works. When the shape has exactly one legal home it simply goes there, and when it has
     * none the game says so — that covers nearly everything a child meets, because the early levels
     * have one or two blanks. Only a genuinely ambiguous shape waits, and then the squares it could go
     * in are lit so the choice is a choice rather than a guess.
     */
    let pending: number | null = null;
    let candidates: number[] = [];

    const boardLayer = new Container();
    const paletteLayer = new Container();
    layer.addChild(boardLayer, paletteLayer);

    const cellNodes = grid.map(() => {
      const holder = new Container();
      const panel = new Graphics();
      holder.addChild(panel);
      boardLayer.addChild(holder);
      return { holder, panel, content: null as Graphics | Text | null };
    });

    const paletteNodes = Array.from({ length: puzzle.size }, () => {
      const holder = new Container();
      const panel = new Graphics();
      holder.addChild(panel);
      holder.eventMode = "static";
      holder.cursor = "pointer";
      paletteLayer.addChild(holder);
      return { holder, panel, content: null as Graphics | Text | null };
    });

    let cellSize = touchMin();
    let resolveSolved: ((v: { grid: number[]; refusals: number }) => void) | null = null;
    let accepting = true;

    function drawCell(index: number): void {
      const node = cellNodes[index];
      if (!node) return;
      const value = grid[index] as number;
      const isBlank = blanks.has(index);
      const isSelected = selected === index;
      const isConflict = conflicts.includes(index);
      // Lit while a shape is waiting for a home, so the choice is between marked squares rather than
      // a hunt across the board.
      const isCandidate = candidates.includes(index);

      const panel = isConflict
        ? mix(COLOR.wrong, COLOR.stage, 0.5)
        : isSelected || isCandidate
          ? mix(accent, COLOR.stage, 0.45)
          : isBlank
            ? mix(COLOR.stage, COLOR.card, 0.16)
            : mix(COLOR.stage, COLOR.stageDeep, 0.35);

      node.panel.clear();
      node.panel
        .roundRect(-cellSize / 2, -cellSize / 2, cellSize, cellSize, cellSize * 0.16)
        .fill(panel);
      if (isSelected || isCandidate) {
        node.panel
          .roundRect(-cellSize / 2, -cellSize / 2, cellSize, cellSize, cellSize * 0.16)
          .stroke({ width: 3, color: accent });
      }

      node.content?.destroy();
      node.content = null;
      if (value !== EMPTY) {
        /**
         * A given is dimmed and a placed symbol is not.
         *
         * The child needs to see at a glance which squares are theirs — otherwise, when they are
         * stuck, they cannot tell what they are allowed to take back.
         */
        node.content = drawSymbol(
          node.holder,
          value,
          cellSize,
          puzzle.digits,
          isBlank ? COLOR.cream : COLOR.muted,
        );
      }
    }

    function drawPalette(): void {
      paletteNodes.forEach((node, value) => {
        const size = Math.min(cellSize * 1.05, touchMin());
        node.panel.clear();
        node.panel
          .roundRect(-size / 2, -size / 2, size, size, size * 0.22)
          .fill(mix(COLOR.stage, COLOR.card, 0.14));
        node.panel
          .roundRect(-size / 2 + size * 0.08, -size / 2 + size * 0.06, size * 0.84, size * 0.14, size * 0.08)
          .fill({ color: 0xffffff, alpha: 0.07 });
        node.content?.destroy();
        node.content = drawSymbol(node.holder, value, size, puzzle.digits, COLOR.cream);

        const half = size / 2 + size * 0.12;
        node.holder.hitArea = {
          contains: (hx: number, hy: number) =>
            hx >= -half && hy >= -half && hx <= half && hy <= half,
        };
      });
    }

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const pad = Math.max(12, w * 0.045);
      const paletteH = touchMin() + pad;
      const gap = Math.max(3, w * 0.008);

      const boardMax = Math.min(w - pad * 2, h - paletteH - pad * 2);
      cellSize = (boardMax - gap * (puzzle.size - 1)) / puzzle.size;
      const boardW = cellSize * puzzle.size + gap * (puzzle.size - 1);
      const boardLeft = (w - boardW) / 2;
      const boardTop = pad + (h - paletteH - pad * 2 - boardW) / 2;

      cellNodes.forEach((node, i) => {
        const col = i % puzzle.size;
        const row = Math.floor(i / puzzle.size);
        /**
         * Boxes are separated by a wider gutter, not by a line.
         *
         * The box boundary is the hardest rule for a child to see, and a drawn grid line competes
         * with the symbols. Pushing the boxes apart makes the grouping visible without adding ink.
         */
        const boxGapX = Math.floor(col / puzzle.boxCols) * gap * 2;
        const boxGapY = Math.floor(row / puzzle.boxRows) * gap * 2;
        node.holder.position.set(
          boardLeft + col * (cellSize + gap) + cellSize / 2 + boxGapX,
          boardTop + row * (cellSize + gap) + cellSize / 2 + boxGapY,
        );
        drawCell(i);

        const half = cellSize / 2;
        node.holder.hitArea = {
          contains: (hx: number, hy: number) =>
            hx >= -half && hy >= -half && hx <= half && hy <= half,
        };
      });

      const paletteSize = Math.min(cellSize * 1.05, touchMin());
      const paletteGap = Math.max(6, w * 0.02);
      const paletteW =
        paletteSize * puzzle.size + paletteGap * (puzzle.size - 1);
      const paletteLeft = (w - paletteW) / 2;
      paletteNodes.forEach((node, i) => {
        node.holder.position.set(
          paletteLeft + i * (paletteSize + paletteGap) + paletteSize / 2,
          h - pad - paletteSize / 2,
        );
      });
      drawPalette();
    }

    /** Squares that already hold `value` in the same row, column or box. */
    function conflictingCells(cell: number, value: number): number[] {
      const size = puzzle.size;
      const row = Math.floor(cell / size);
      const col = cell % size;
      const boxTop = Math.floor(row / puzzle.boxRows) * puzzle.boxRows;
      const boxLeft = Math.floor(col / puzzle.boxCols) * puzzle.boxCols;

      const hits: number[] = [];
      for (let i = 0; i < grid.length; i++) {
        if (i === cell || grid[i] !== value) continue;
        const r = Math.floor(i / size);
        const c = i % size;
        const sameBox =
          r >= boxTop && r < boxTop + puzzle.boxRows && c >= boxLeft && c < boxLeft + puzzle.boxCols;
        if (r === row || c === col || sameBox) hits.push(i);
      }
      return hits;
    }

    /** Empty squares this shape may legally go in, right now. */
    function legalHomes(value: number): number[] {
      return [...blanks].filter(
        (cell) => grid[cell] === EMPTY && opts.placementAllowed(puzzle, grid, cell, value),
      );
    }

    /**
     * Refused with no square in mind: the shape fits nowhere on the board.
     *
     * Lights every square already holding it, which is the same lesson the per-square refusal teaches —
     * "not anywhere, because there and there" — without needing the child to have picked a target
     * first.
     */
    async function refuseEverywhere(value: number): Promise<void> {
      refusals++;
      missTone();
      conflicts = grid.flatMap((held, i) => (held === value ? [i] : []));
      layout();
      for (const i of conflicts) {
        const lit = cellNodes[i];
        if (lit) {
          glow(glowLayer, lit.holder.position.x, lit.holder.position.y, cellSize * 0.7, COLOR.wrong, 650);
        }
      }
      await idle(520);
      conflicts = [];
      layout();
    }

    async function place(value: number, at?: number): Promise<void> {
      const cell = at ?? selected;
      if (cell === null) return;

      if (opts.placementAllowed(puzzle, grid, cell, value)) {
        grid[cell] = value;
        selected = null;
        pending = null;
        candidates = [];
        conflicts = [];
        cellTone(value, { volume: 0.14 });
        const node = cellNodes[cell];
        layout();
        if (node) {
          ripple(fx, node.holder.position.x, node.holder.position.y, accent, cellSize);
          void pop(node.holder, { scale: 1.12 });
        }

        if (blanks.size > 0 && [...blanks].every((i) => grid[i] !== EMPTY)) {
          accepting = false;
          successChime();
          for (const i of blanks) {
            const filled = cellNodes[i];
            if (filled) {
              burst(fx, filled.holder.position.x, filled.holder.position.y, [
                accent,
                lighten(accent, 0.4),
                COLOR.correct,
              ]);
            }
          }
          await idle(700);
          resolveSolved?.({ grid: [...grid], refusals });
        }
        return;
      }

      /**
       * Refused, and the reason is shown rather than merely signalled.
       *
       * Lighting the squares that already hold the symbol is the entire teaching content of this
       * game: "not here, because there" is a rule a child can see once and apply forever, and a
       * buzz with no explanation teaches only that the game is arbitrary.
       */
      refusals++;
      missTone();
      conflicts = conflictingCells(cell, value);
      layout();
      const node = cellNodes[cell];
      if (node) await nudge(node.holder);
      for (const i of conflicts) {
        const lit = cellNodes[i];
        if (lit) glow(glowLayer, lit.holder.position.x, lit.holder.position.y, cellSize * 0.7, COLOR.wrong, 650);
      }
      await idle(520);
      conflicts = [];
      layout();
    }

    cellNodes.forEach((node, i) => {
      if (!blanks.has(i)) return;
      node.holder.eventMode = "static";
      node.holder.cursor = "pointer";
      node.holder.on("pointertap", () => {
        if (!accepting) return;
        if (grid[i] !== EMPTY) {
          // Tapping a square the child filled takes it back. The escape from a dead end.
          grid[i] = EMPTY;
          selected = i;
          pending = null;
          candidates = [];
          tick();
          layout();
          return;
        }
        // A shape is waiting for a home and this is one of them: finish the move.
        if (pending !== null && candidates.includes(i)) {
          const value = pending;
          pending = null;
          candidates = [];
          void place(value, i);
          return;
        }
        selected = selected === i ? null : i;
        pending = null;
        candidates = [];
        tick();
        layout();
      });
    });

    paletteNodes.forEach((node, value) => {
      node.holder.on("pointertap", () => {
        if (!accepting) return;

        // A square is already chosen: put it there, or refuse there. The original two-step order,
        // which still works because some children will do it that way.
        if (selected !== null) {
          void place(value);
          return;
        }

        /**
         * No square chosen — the shape finds its own home.
         *
         * This is what a child actually does: they see the shape that is missing and tap it. Making
         * them pick the hole first was an order imposed by the implementation rather than by the game.
         */
        const homes = legalHomes(value);
        if (homes.length === 0) {
          void refuseEverywhere(value);
          return;
        }
        if (homes.length === 1) {
          /**
           * One legal square means it is the RIGHT square, so this cannot make a move for the child
           * that they then have to undo.
           *
           * In the unique solution the shape occupies some empty square, which must be one of its legal
           * candidates; with one candidate, that is the one. A unique candidate that is not the
           * solution's square can only exist on a grid already inconsistent with the solution, which a
           * child reaches only by placing something legal and wrong themselves — and undoes by tapping
           * it. Proved across every level and form in `packages/tasks/src/sudoku.test.ts`.
           */
          void place(value, homes[0] as number);
          return;
        }
        /**
         * It fits in more than one square, so the child has to say which.
         *
         * Guessing for them would be worse than asking: on a six-by-six with several blanks a shape
         * often has two legal homes and only one is right in the end, and putting it in the wrong one
         * silently would send the child down a path they then have to undo without knowing why.
         */
        pending = value;
        candidates = homes;
        tick();
        void pop(node.holder, { scale: 1.1 });
        layout();
      });
    });

    layout();
    app.renderer.on("resize", layout);

    if (!prefersReducedMotion()) {
      cellNodes.forEach((node, i) => {
        node.holder.scale.set(0);
        void idle(60 + i * 12).then(() =>
          animate(260, (t) => node.holder.scale.set(easeOutBack(t))),
        );
      });
    }
    tick();

    const solved = new Promise<{ grid: number[]; refusals: number }>((resolve) => {
      resolveSolved = resolve;
    });

    return {
      solved,
      demonstrate: async () => {
        for (const cell of puzzle.blanks) {
          if (!accepting || opts.signal?.aborted) return;
          const value = puzzle.solution[cell] as number;
          /**
           * The shape in the palette is lit first, then it lands.
           *
           * A pause between the two is what makes the demonstration readable as cause and effect
           * rather than as the board filling itself in. The shape is tapped with no square chosen,
           * which is exactly what a child will do.
           */
          const node = paletteNodes[value];
          if (node) {
            tick();
            await pop(node.holder, { scale: 1.18 });
          }
          await idle(600);
          if (!accepting || opts.signal?.aborted) return;
          await place(value, cell);
          await idle(500);
        }
      },
      cleanup: () => {
        accepting = false;
        app.renderer.off("resize", layout);
        layer.destroy({ children: true });
      },
    };
  }

  /**
   * The wordless demonstration: the easiest grid, solved in front of the child.
   *
   * Level one has a single blank, so what is shown is exactly the rule — tap the square, tap the
   * shape — with nothing else happening at the same time. Drawn from `DEMO_FORM`, which is absent from
   * every task's `formIds`, so no scored trial is ever given away.
   */
  async function playTutorial(): Promise<void> {
    let choice: "replay" | "start" = "start";
    do {
      const demo = opts.generatePuzzle(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const shown = present(demo);
      await idle(700);
      if (!opts.signal?.aborted) {
        await shown.demonstrate();
        await untilAbandoned(shown.solved, opts.signal);
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: SudokuTaskResult["reason"] = "completed";

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();
    for (;;) {
      const puzzle = opts.generatePuzzle(task, formId, trialIndex, difficulty);

      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // The grid as shown. A puzzle stays re-checkable from the record without the generator that
        // produced it, which is the same guarantee every other task's stimulus field gives.
        stimulus: puzzle.given,
      });
      recorder.setInTrial(true);

      const startedAt = performance.now();
      const trial = present(puzzle);
      const outcome = await untilSettled(trial.solved, opts.signal, task.measurement.trialTimeoutMs);
      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        /**
         * Stopping and leaving both end the sitting, and only one of them is recorded.
         *
         * A timeout means the child put the phone down mid-trial. It is `abandoned` rather than a
         * wrong answer on purpose: we know they stopped and we do not know they could not do it, and
         * scoring silence as failure is the mistake `stopAfterErrors` used to make.
         */
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", {
            trialIndex,
            elapsedMs: performance.now() - startedAt,
          });
        }
        trial.cleanup();
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }
      const responseMs = performance.now() - startedAt;
      trial.cleanup();

      const correct = opts.scoreTrial(puzzle, outcome.grid, outcome.refusals);
      if (correct) correctCount++;

      await recorder.record("trial_response", {
        trialIndex,
        response: puzzle.blanks.map((cell) => outcome.grid[cell] ?? EMPTY),
        correct,
        responseMs,
        /**
         * Refused placements, recorded as corrections.
         *
         * The single most informative number this game produces: two children can both finish the
         * same grid, one having reasoned it out and one having tried every symbol in every square,
         * and nothing about the finished board tells them apart.
         */
        corrections: outcome.refusals,
        attempts: puzzle.blanks.length + outcome.refusals,
      });

      /**
       * No speed check here.
       *
       * `detectImplausibleSpeed` is calibrated per stimulus item for tasks answered with one tap. A
       * whole grid takes minutes, so the floor is never approached and applying it would be a check
       * that can only ever pass.
       */
      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(240);
    }
  } catch {
    reason = "error";
  } finally {
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
